// CLOSED-LOOPS: the Commitments card's quick-action modals — one text answer (decline, hand in,
// send back, withdraw, a decision's note), "Can't do it" (the failure report with 2-4 options, one
// recommended), forward to another member, a new due time, and the read-only details (the full
// result, evidence, what done looks like, the last feedback).
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { Commitment, CommitmentActionBody } from "./commitment-queries";

type Send = (body: CommitmentActionBody) => void;

function Shell({
  title,
  submit,
  disabled,
  onCancel,
  onSubmit,
  children,
}: {
  title: string;
  submit: string;
  disabled: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  const go = (e: FormEvent) => {
    e.preventDefault();
    if (!disabled) onSubmit();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={go} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {children}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={disabled}>
              {submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export type TextAsk = { title: string; label: string; submit: string; required: boolean };

/** One text answer; `onSubmit` gets it trimmed ("" when left out). */
export function TextDialog({
  ask,
  pending,
  onCancel,
  onSubmit,
}: {
  ask: TextAsk;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}) {
  const [text, setText] = useState("");
  const value = text.trim();
  return (
    <Shell
      title={ask.title}
      submit={ask.submit}
      disabled={pending || (ask.required && !value)}
      onCancel={onCancel}
      onSubmit={() => onSubmit(value)}
    >
      <Field>
        <FieldLabel htmlFor="commitment-text">{ask.label}</FieldLabel>
        <Textarea
          id="commitment-text"
          rows={3}
          maxLength={1000}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
    </Shell>
  );
}

const CAUSES = [
  ["tool_error", "Something broke"],
  ["blocked_on_input", "Missing input"],
  ["budget_spent", "Out of budget"],
  ["deadline_missed", "Out of time"],
  ["loop", "Going in circles"],
] as const;

type Draft = { label: string; detail: string };

/** "Can't do it": what happened, what was tried, what there is so far, and 2-4 options. */
export function FailDialog({
  pending,
  onCancel,
  onSend,
}: {
  pending: boolean;
  onCancel: () => void;
  onSend: Send;
}) {
  const [cause, setCause] = useState<(typeof CAUSES)[number][0]>("tool_error");
  const [what, setWhat] = useState("");
  const [tried, setTried] = useState("");
  const [partial, setPartial] = useState("");
  const [why, setWhy] = useState("");
  const [options, setOptions] = useState<Draft[]>([
    { label: "", detail: "" },
    { label: "", detail: "" },
  ]);
  const [pick, setPick] = useState(0);
  const filled = options.filter((o) => o.label.trim());
  const ok = what.trim() && filled.length >= 2 && options[pick]?.label.trim();
  const edit = (i: number, patch: Partial<Draft>) =>
    setOptions((l) => l.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const send = () => {
    const chosen = options[pick];
    const list = options
      .map((o, i) => ({ ...o, i }))
      .filter((o) => o.label.trim())
      .map((o) => ({
        label: o.label.trim(),
        ...(o.detail.trim() ? { detail: o.detail.trim() } : {}),
        recommended: o.i === pick,
      }));
    onSend({
      action: "fail",
      failure: {
        cause,
        what_happened: what.trim(),
        tried: tried
          .split("\n")
          .map((t) => t.trim())
          .filter(Boolean)
          .slice(0, 10),
        ...(partial.trim() ? { partial: [{ label: partial.trim() }] } : {}),
        options: list,
        recommendation: why.trim() || `Recommended: ${chosen?.label.trim() ?? ""}`,
      },
    });
  };
  return (
    <Shell
      title="Can't do it"
      submit="Send"
      disabled={pending || !ok}
      onCancel={onCancel}
      onSubmit={send}
    >
      <div className="grid max-h-[60vh] gap-3 overflow-y-auto">
        <Field>
          <FieldLabel htmlFor="fail-cause">Why</FieldLabel>
          <NativeSelect
            id="fail-cause"
            size="sm"
            value={cause}
            onChange={(e) => setCause(e.target.value as typeof cause)}
          >
            {CAUSES.map(([v, l]) => (
              <NativeSelectOption key={v} value={v}>
                {l}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor="fail-what">What happened</FieldLabel>
          <Input
            id="fail-what"
            maxLength={300}
            value={what}
            onChange={(e) => setWhat(e.target.value)}
          />
        </Field>
        <Field>
          <span className="flex items-center">
            <FieldLabel htmlFor="fail-tried">What was tried</FieldLabel>
            <InfoTip label="One per line">One attempt per line.</InfoTip>
          </span>
          <Textarea
            id="fail-tried"
            rows={2}
            value={tried}
            onChange={(e) => setTried(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="fail-partial">So far (optional)</FieldLabel>
          <Input
            id="fail-partial"
            maxLength={160}
            value={partial}
            onChange={(e) => setPartial(e.target.value)}
          />
        </Field>
        <fieldset className="grid gap-2">
          <legend className="mb-1 flex items-center text-sm font-medium">
            Options
            <InfoTip label="About options">
              2 to 4 ways forward. Mark the one you recommend.
            </InfoTip>
          </legend>
          {options.map((o, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: drafts have no id; rows only append
            <div key={i} className="grid gap-1 rounded-sm border border-subtle p-2">
              <Input
                aria-label={`Option ${i + 1}`}
                placeholder={`Option ${i + 1}`}
                maxLength={160}
                value={o.label}
                onChange={(e) => edit(i, { label: e.target.value })}
              />
              <Input
                aria-label={`Option ${i + 1} detail`}
                placeholder="Detail (optional)"
                maxLength={400}
                value={o.detail}
                onChange={(e) => edit(i, { detail: e.target.value })}
              />
              <label className="flex items-center gap-1.5 text-xs">
                <input
                  type="radio"
                  name="fail-recommended"
                  checked={pick === i}
                  onChange={() => setPick(i)}
                />
                Recommended
              </label>
            </div>
          ))}
          {options.length < 4 ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="justify-self-start"
              onClick={() => setOptions((l) => [...l, { label: "", detail: "" }])}
            >
              Add option
            </Button>
          ) : null}
        </fieldset>
        <Field>
          <FieldLabel htmlFor="fail-why">Why that one (optional)</FieldLabel>
          <Input
            id="fail-why"
            maxLength={300}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
          />
        </Field>
      </div>
    </Shell>
  );
}

/** Forward to another member of the company (a person or a DE). */
export function ForwardDialog({
  api,
  co,
  c,
  pending,
  onCancel,
  onSend,
}: {
  api: Api;
  co: string;
  c: Commitment;
  pending: boolean;
  onCancel: () => void;
  onSend: Send;
}) {
  const members = useQuery({
    queryKey: ["company", co, "commitments", "members"],
    queryFn: () => api.client.people.list({ params: { co }, query: { limit: 200 } }),
    staleTime: 60_000,
  });
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const list = (members.data?.data ?? []).filter(
    (m) => m.status === "active" && m.id !== c.owner.membership_id,
  );
  return (
    <Shell
      title="Forward"
      submit="Forward"
      disabled={pending || !to}
      onCancel={onCancel}
      onSubmit={() =>
        onSend({
          action: "forward",
          to_membership_id: to,
          ...(note.trim() ? { note: note.trim() } : {}),
        })
      }
    >
      <Field>
        <FieldLabel htmlFor="forward-to">To</FieldLabel>
        <NativeSelect id="forward-to" value={to} onChange={(e) => setTo(e.target.value)}>
          <NativeSelectOption value="">Pick someone</NativeSelectOption>
          {list.map((m) => (
            <NativeSelectOption key={m.id} value={m.id}>
              {m.kind === "digital_employee" ? `${m.display_name} (DE)` : m.display_name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <Field>
        <FieldLabel htmlFor="forward-note">Note (optional)</FieldLabel>
        <Input
          id="forward-note"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Shell>
  );
}

/** `YYYY-MM-DDTHH:mm` in local time, for a datetime-local input. */
const localInput = (ms: number) => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** A new due time (later than now). */
export function ExtendDialog({
  c,
  pending,
  onCancel,
  onSend,
}: {
  c: Commitment;
  pending: boolean;
  onCancel: () => void;
  onSend: Send;
}) {
  const [at, setAt] = useState(() =>
    localInput(Math.max(Date.parse(c.due_at), Date.now()) + 86_400_000),
  );
  const [note, setNote] = useState("");
  const ms = Date.parse(at);
  return (
    <Shell
      title="New due time"
      submit="Extend"
      disabled={pending || !(ms > Date.now())}
      onCancel={onCancel}
      onSubmit={() =>
        onSend({
          action: "extend",
          due_at: new Date(ms).toISOString(),
          ...(note.trim() ? { note: note.trim() } : {}),
        })
      }
    >
      <Field>
        <FieldLabel htmlFor="extend-at">Due</FieldLabel>
        <Input
          id="extend-at"
          type="datetime-local"
          value={at}
          onChange={(e) => setAt(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="extend-note">Note (optional)</FieldLabel>
        <Input
          id="extend-note"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Shell>
  );
}

function Part({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-medium text-muted-foreground">{label}</h3>
      <div className="text-[13px] whitespace-pre-wrap">{children}</div>
    </div>
  );
}

/** Everything about one commitment, read-only (its moves are on its row). */
export function DetailDialog({
  c,
  chip,
  onClose,
}: {
  c: Commitment;
  chip: { label: string; tip: string };
  onClose: () => void;
}) {
  const chosen = c.options?.find((o) => o.key === c.chosen_option);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg" data-slot="commitment-detail">
        <DialogHeader>
          <DialogTitle>{c.intent}</DialogTitle>
        </DialogHeader>
        <div className="grid max-h-[65vh] gap-3 overflow-y-auto">
          <p className="flex items-center gap-1 text-[13px] text-secondary">
            <Badge>{chip.label}</Badge>
            <InfoTip label={`About ${chip.label}`}>{chip.tip}</InfoTip>
            <span>
              {c.requester.name} → {c.owner.name}
            </span>
          </p>
          {c.expected_outcome ? <Part label="Done looks like">{c.expected_outcome}</Part> : null}
          {c.done_when.length ? (
            <Part label="Done when">
              <ul className="list-disc pl-4">
                {c.done_when.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            </Part>
          ) : null}
          {c.result ? <Part label="Result">{c.result}</Part> : null}
          {c.evidence.length ? (
            <Part label="Evidence">
              <ul className="list-disc pl-4">
                {c.evidence.map((e) => (
                  <li key={`${e.kind}:${e.ref}`}>
                    {e.kind === "url" ? (
                      <a href={e.ref} target="_blank" rel="noreferrer" className="underline">
                        {e.label ?? e.ref}
                      </a>
                    ) : (
                      (e.label ?? e.ref)
                    )}
                  </li>
                ))}
              </ul>
            </Part>
          ) : null}
          {c.feedback ? <Part label="Last feedback">{c.feedback}</Part> : null}
          {c.failure_text ? <Part label="What happened">{c.failure_text}</Part> : null}
          {chosen ? (
            <Part label="Chosen">
              {chosen.label}
              {c.choice_note ? ` — ${c.choice_note}` : ""}
            </Part>
          ) : null}
          {c.state_reason ? <Part label="Reason">{c.state_reason}</Part> : null}
        </div>
        <DialogFooter>
          <DialogClose render={<Button type="button" variant="outline" />}>Close</DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
